/**
 * Single source of truth for the "late delivery not allowed" guard.
 *
 * A student must NOT be able to deliver a test/activity once the deadline
 * (`dueDate` / `deliver_at`) has passed when late delivery is disabled
 * (`canDeliverLate === false`). This predicate is shared by both the classic
 * test player (AnswerTest) and the Quizz player so the guard logic is identical.
 */

/** A deadline can arrive as a Date, an ISO/DB string, a timestamp, or be absent. */
export type DeadlineInput = Date | string | number | null | undefined;

/**
 * MySQL's zero date: the DB can send it for "no deadline" instead of NULL.
 * V8 parses it as Invalid Date (verified with `node -e "new Date('0000-00-00 00:00:00')"`),
 * but it is rejected explicitly so the predicate never depends on engine date parsing.
 */
const MYSQL_ZERO_DATE = '0000-00-00 00:00:00';

type IsDeliveryBlockedParams = {
  dueDate: DeadlineInput;
  canDeliverLate: boolean;
  /** Reference time; defaults to now. Injectable for testing. */
  now?: Date;
};

/** Normalizes any supported input into a Date, or null when it is absent/unparseable. */
function toValidDate(dueDate: DeadlineInput): Date | null {
  if (!dueDate) return null;

  const deadline = dueDate instanceof Date ? dueDate : new Date(dueDate);
  if (Number.isNaN(deadline.getTime())) return null;

  return deadline;
}

/**
 * Returns true when the student can no longer deliver because the deadline has
 * passed and late delivery is not allowed.
 *
 * Guard: `Boolean(dueDate) && !canDeliverLate && now >= dueDate`.
 * When there is no deadline (`dueDate` null/undefined) delivery is never blocked.
 */
export function isDeliveryBlocked({ dueDate, canDeliverLate, now = new Date() }: IsDeliveryBlockedParams): boolean {
  if (canDeliverLate) return false;

  const deadline = toValidDate(dueDate);
  if (!deadline) return false;

  return now.getTime() >= deadline.getTime();
}

/**
 * Returns true when the activity actually has a usable deadline.
 *
 * "No deadline" covers null/undefined, the empty string, an unparseable date and
 * MySQL's zero date. Used to gate options that only make sense with a due date
 * (e.g. "allow late delivery", TAN-10453).
 */
export function hasDueDate(dueDate: DeadlineInput): boolean {
  if (dueDate === MYSQL_ZERO_DATE) return false;

  return toValidDate(dueDate) !== null;
}

export default isDeliveryBlocked;
